import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { interval } from 'rxjs';

export type CountdownVariant = 'badge' | 'pill' | 'banner' | 'inline';
export type CountdownUrgency = 'normal' | 'warning' | 'critical' | 'expired';

@Component({
  selector: 'aura-countdown',
  templateUrl: './countdown.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIconModule],
})
export class AuraCountdownComponent {
  private readonly destroyRef = inject(DestroyRef);

  /**
   * Date or timestamp target to count down to.
   */
  readonly targetDate = input.required<Date | string | number | null>();

  /**
   * Total reference duration for urgency and progress bar (default: 24 hours in ms).
   */
  readonly totalDurationMs = input<number>(24 * 60 * 60 * 1000);

  /**
   * Threshold for warning state (amber) in ms (default: 4 hours).
   */
  readonly urgentThresholdMs = input<number>(4 * 60 * 60 * 1000);

  /**
   * Threshold for critical state (tomato / pulse) in ms (default: 1 hour).
   */
  readonly criticalThresholdMs = input<number>(1 * 60 * 60 * 1000);

  /**
   * Visual presentation variant.
   */
  readonly variant = input<CountdownVariant>('badge');

  /**
   * Size of the countdown badge/text.
   */
  readonly size = input<'xs' | 'sm' | 'md'>('sm');

  /**
   * Optional prefix label (e.g. 'Répondre avant :' or 'Temps restant :').
   */
  readonly prefix = input<string>('');

  /**
   * Text to display when countdown reaches zero.
   */
  readonly expiredText = input<string>('Délai expiré');

  /**
   * Whether to show an icon.
   */
  readonly showIcon = input<boolean>(true);

  /**
   * Whether to display a micro progress bar (only for banner and badge variants).
   */
  readonly showProgress = input<boolean>(false);

  /**
   * Emitted when the countdown transitions from active to expired.
   */
  readonly expired = output<void>();

  /**
   * Internal tick signal (timestamp).
   */
  private readonly now = signal<number>(Date.now());
  private hasEmittedExpired = false;

  constructor() {
    interval(1000)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.now.set(Date.now());
        if (this.isExpired() && !this.hasEmittedExpired) {
          this.hasEmittedExpired = true;
          this.expired.emit();
        }
      });
  }

  /**
   * Normalized target timestamp in milliseconds.
   */
  readonly targetTimestamp = computed<number | null>(() => {
    const raw = this.targetDate();
    if (!raw) return null;
    if (typeof raw === 'number') return raw;
    const parsed = new Date(raw).getTime();
    return Number.isNaN(parsed) ? null : parsed;
  });

  /**
   * Remaining time in milliseconds.
   */
  readonly remainingMs = computed<number>(() => {
    const target = this.targetTimestamp();
    if (!target) return 0;
    return target - this.now();
  });

  /**
   * Whether the countdown is expired.
   */
  readonly isExpired = computed<boolean>(() => {
    const target = this.targetTimestamp();
    if (!target) return false;
    return this.remainingMs() <= 0;
  });

  /**
   * Urgency category for contextual styling.
   */
  readonly urgency = computed<CountdownUrgency>(() => {
    if (this.isExpired()) return 'expired';
    const rem = this.remainingMs();
    if (rem <= this.criticalThresholdMs()) return 'critical';
    if (rem <= this.urgentThresholdMs()) return 'warning';
    return 'normal';
  });

  /**
   * Progress percentage (0% to 100%) of remaining time relative to totalDurationMs.
   */
  readonly progressPercent = computed<number>(() => {
    if (this.isExpired()) return 0;
    const total = this.totalDurationMs();
    if (total <= 0) return 0;
    const rem = this.remainingMs();
    return Math.max(0, Math.min(100, (rem / total) * 100));
  });

  /**
   * Hours, minutes, and seconds breakdowns.
   */
  readonly timeParts = computed(() => {
    const ms = Math.max(0, this.remainingMs());
    const totalSeconds = Math.floor(ms / 1000);
    const totalMinutes = Math.floor(totalSeconds / 60);
    const totalHours = Math.floor(totalMinutes / 60);
    const days = Math.floor(totalHours / 24);

    return {
      days,
      hours: totalHours % 24,
      totalHours,
      minutes: totalMinutes % 60,
      seconds: totalSeconds % 60,
    };
  });

  /**
   * Human-readable formatted string in French Sentence case.
   */
  readonly formattedTime = computed<string>(() => {
    if (this.isExpired()) {
      return this.expiredText();
    }

    const { days, totalHours, minutes, seconds } = this.timeParts();

    if (days > 0) {
      const remainingHours = totalHours % 24;
      return `${days} j ${remainingHours} h ${minutes} min`;
    }

    const pad = (n: number) => n.toString().padStart(2, '0');

    if (totalHours > 0) {
      return `${totalHours}h ${pad(minutes)}m ${pad(seconds)}s`;
    }

    if (minutes > 0) {
      return `${minutes}m ${pad(seconds)}s`;
    }

    return `${seconds}s`;
  });

  /**
   * Relevant icon name depending on urgency and expiration.
   */
  readonly iconName = computed<string>(() => {
    switch (this.urgency()) {
      case 'critical':
        return 'alarm';
      case 'warning':
        return 'hourglass_bottom';
      case 'expired':
        return 'timer_off';
      case 'normal':
      default:
        return 'timer';
    }
  });

  /**
   * Style classes according to variant and urgency.
   */
  readonly containerClasses = computed<string>(() => {
    const v = this.variant();
    const u = this.urgency();
    const s = this.size();

    const sizeClass =
      s === 'xs'
        ? 'text-[11px] gap-1 px-2 py-0.5'
        : s === 'md'
          ? 'text-sm gap-2 px-3.5 py-1.5'
          : 'text-xs gap-1.5 px-2.5 py-1';

    if (v === 'inline') {
      return `inline-flex items-center gap-1.5 font-medium ${
        u === 'critical'
          ? 'text-tomato-11 font-semibold'
          : u === 'warning'
            ? 'text-amber-11 font-semibold'
            : u === 'expired'
              ? 'text-neutral-10'
              : 'text-primary-11 font-medium'
      }`;
    }

    const colorClass =
      u === 'critical'
        ? 'bg-tomato-2 text-tomato-11 border-tomato-4/60 dark:bg-tomato-9/15 dark:border-tomato-4/40 animate-pulse'
        : u === 'warning'
          ? 'bg-amber-2 text-amber-11 border-amber-4/60 dark:bg-amber-9/15 dark:border-amber-4/40'
          : u === 'expired'
            ? 'bg-neutral-3 text-neutral-10 border-neutral-4/60 dark:bg-neutral-8/20 dark:text-neutral-11'
            : 'bg-primary-2 text-primary-11 border-primary-4/60 dark:bg-primary-9/15 dark:border-primary-4/40';

    if (v === 'pill') {
      return `inline-flex items-center rounded-full font-semibold border ${sizeClass} ${colorClass}`;
    }

    if (v === 'banner') {
      return `flex items-center justify-between rounded-xl font-medium border p-3 ${colorClass}`;
    }

    // Default 'badge'
    return `inline-flex items-center rounded-lg font-semibold border ${sizeClass} ${colorClass}`;
  });
}
